import BaseApi, { API_BASE_URL, ApiResponse } from './base';
import { FileMetadata } from './files';
import { Glossary } from './glossaries';

export interface QuestionOption {
  text: string;
}

export interface QuizQuestion {
  id: string;
  title: string;
  options: QuestionOption[];
  correctAnswers: number[]; // Array of correct answer indices (0-based)
  allowMultiple: boolean; // Whether multiple answers can be selected
  explanation?: string;
}

export interface QuestionConfig {
  minimumScore?: number; // Minimum score percentage required to pass (default: 60)
  randomizeOrder?: boolean; // Whether to randomize question order
  selectRandomCount?: number; // Number of random questions to select from pool
}

export interface QuestionStepContent {
  questions?: QuizQuestion[];
  questionConfig?: QuestionConfig;
}

export enum GuideDifficulty {
  BEGINNER = 'beginner',
  INTERMEDIATE = 'intermediate',
  ADVANCED = 'advanced',
}

export enum GuideLanguage {
  EN = 'en',
  ES = 'es',
}

export enum GuideStepType {
  RICH_TEXT = 'RICH_TEXT',
  VIDEO = 'VIDEO',
  IMAGE = 'IMAGE',
  QUESTION = 'QUESTION',
  PDF = 'PDF',
  CODE = 'CODE',
}

export interface StepTranslation {
  id: string;
  language: GuideLanguage;
  title?: string;
  content?: any;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface GuideStep {
  id: string;
  position: number;
  type: GuideStepType;
  translations: StepTranslation[];
  glossaries?: Glossary[]; // Glossary data from step snapshot
  questionConfig?: QuestionConfig; // Question configuration (applies to all translations)
  createdAt: string;
  updatedAt: string;
}

export interface GuideTranslation {
  id: string;
  language: GuideLanguage;
  title: string;
  description?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface Guide {
  id: string;
  author?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    role: string;
  };
  latestPublishedVersionId?: string;
  product?: any;
  category?: any;
  difficulty: GuideDifficulty;
  isPublished: boolean;
  isRecommended?: boolean;
  estimatedTime?: number;
  translations: GuideTranslation[];
  steps: GuideStep[];
  createdAt: string;
  updatedAt: string;
  job?: {
    id: string;
    status: string;
    payload: any;
    errorMessage?: string;
    createdAt: string;
    updatedAt: string;
  } | null;
}

export interface CreateGuideRequest {
  title: string;
  description?: string;
  difficulty?: GuideDifficulty;
  estimatedTime?: string;
  productId?: string;
  categoryId?: string;
  fileId?: string;
}

export interface UpdateGuideRequest {
  title?: string;
  description?: string;
  difficulty?: GuideDifficulty;
  estimatedTime?: number;
  productId?: string;
  categoryId?: string;
  isRecommended?: boolean;
}

export enum ContentDepth {
  SIMPLIFIED = 'simplified',
  TECHNICAL = 'technical',
  IN_DEPTH = 'in-depth',
}

export interface GenerateGuideFromPdfRequest {
  title: string;
  difficulty?: GuideDifficulty;
  fileId: string;
  processImages?: boolean;
  contentDepth?: ContentDepth;
}

export interface CreateGuideResponse {
  id: string;
  jobId?: string;
}

export interface StepContent {
  text?: string;
  images?: string[];
  videos?: string[];
  questions?: QuizQuestion[];
  pdf?: FileMetadata;
  video?: FileMetadata;
  image?: FileMetadata;
  code?: string;
  audio?: string;
  videoUrl?: string;
}

export interface AddStepRequest {
  position: number;
  type: GuideStepType;
  title?: string;
  content?: StepContent;
  questionConfig?: QuestionConfig;
}

class GuidesApi extends BaseApi {
  constructor(baseURL: string = API_BASE_URL) {
    super(baseURL);
  }

  async getGuides(params?: {
    cursor?: string;
    limit?: number;
    language?: string;
    productId?: string;
    categoryId?: string;
    categoryIds?: string[];
    isPublished?: boolean;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    useDraftData?: boolean;
  }): Promise<ApiResponse<Guide[]>> {
    const processedParams = { ...params };
    // Convert categoryIds array to comma-separated string for query params
    if (processedParams?.categoryIds && processedParams.categoryIds.length > 0) {
      (processedParams as Record<string, unknown>).categoryIds = processedParams.categoryIds.join(',');
    }
    return this.get<Guide[]>(`/guides`, {
      params: processedParams || {},
    });
  }

  async getGuide(id: string): Promise<ApiResponse<Guide>> {
    return this.get<Guide>(`/guides/${id}`);
  }

  async getGuidesByCategory(category: string, page: number = 1, limit: number = 10): Promise<ApiResponse<Guide[]>> {
    return this.get(`/guides/category/${category}`, { params: { page, limit } });
  }

  async createGuide(data: CreateGuideRequest): Promise<ApiResponse<CreateGuideResponse>> {
    return this.post('/guides', data);
  }

  async generateGuideFromPdf(data: GenerateGuideFromPdfRequest): Promise<ApiResponse<CreateGuideResponse>> {
    return this.post('/guides/generate/pdf', data);
  }

  async updateGuide(id: string, data: UpdateGuideRequest, lang: GuideLanguage): Promise<ApiResponse<Guide>> {
    return this.patch<Guide>(`/guides/${id}?lang=${lang}`, data);
  }

  async updateGuideTranslation(
    id: string,
    lang: string,
    data: UpdateGuideRequest,
  ): Promise<ApiResponse<GuideTranslation>> {
    return this.patch<GuideTranslation>(`/guides/${id}/translations/${lang}`, data);
  }

  async addStep(id: string, data: AddStepRequest): Promise<ApiResponse<GuideStep>> {
    return this.post(`/guides/${id}/steps`, data);
  }

  async updateStep(
    id: string,
    stepId: string,
    data: Partial<AddStepRequest>,
    lang?: GuideLanguage,
  ): Promise<ApiResponse<GuideStep>> {
    const url = lang ? `/guides/${id}/steps/${stepId}?lang=${lang}` : `/guides/${id}/steps/${stepId}`;
    return this.patch<GuideStep>(url, data);
  }

  async deleteStep(id: string, stepId: string): Promise<ApiResponse<{}>> {
    return this.delete<{}>(`/guides/${id}/steps/${stepId}`);
  }

  async updateStepTranslation(
    id: string,
    stepId: string,
    lang: string,
    data: { title?: string; content?: any },
  ): Promise<ApiResponse<StepTranslation>> {
    return this.patch<StepTranslation>(`/guides/${id}/steps/${stepId}/translations/${lang}`, data);
  }

  async getLatestPublishedVersion(id: string): Promise<ApiResponse<any>> {
    return this.get(`/guides/${id}/versions/published`);
  }

  async publishGuide(id: string): Promise<ApiResponse<any>> {
    return this.post(`/guides/${id}/publish`, {});
  }

  async unpublishGuide(id: string): Promise<ApiResponse<any>> {
    return this.post(`/guides/${id}/unpublish`, {});
  }

  async deleteGuide(id: string): Promise<ApiResponse<{}>> {
    return this.delete<{}>(`/guides/${id}`);
  }

  async getGuideStats(): Promise<ApiResponse<{ totalGuides: number; totalPublished: number; totalDrafts: number }>> {
    return this.get<{ totalGuides: number; totalPublished: number; totalDrafts: number }>(`/guides/stats`);
  }

  async translateGuide(id: string, targetLanguage: GuideLanguage): Promise<ApiResponse<Guide>> {
    return this.post<Guide>(`/guides/${id}/translate`, { targetLanguage });
  }

  async submitQuizAnswers(
    guideId: string,
    stepId: string,
    answers: Record<string, number | number[]>,
    displayedQuestionIds?: string[],
  ): Promise<ApiResponse<{ score: number; minimumScore: number }>> {
    return this.post<{ score: number; minimumScore: number }>(`/guides/${guideId}/steps/${stepId}/quiz/submit`, {
      answers,
      displayedQuestionIds,
    });
  }

  async getStepAudio(
    stepId: string,
    language: GuideLanguage,
  ): Promise<ApiResponse<{ audioUrl: string; isCached: boolean }>> {
    return this.get<{ audioUrl: string; isCached: boolean }>(`/guides/steps/${stepId}/audio?language=${language}`);
  }

  async getGuideProgress(
    guideId: string,
    language: GuideLanguage,
  ): Promise<
    ApiResponse<{
      id: string;
      guideId: string;
      currentStepId: string | null;
      completedStepIds: string[];
      quizScores: Record<string, number>;
      isCompleted: boolean;
      startTime: string | null;
      language: string;
      createdAt: string;
      updatedAt: string;
    }>
  > {
    return this.get(`/guides/${guideId}/progress?language=${language}`);
  }

  async updateGuideProgress(
    guideId: string,
    language: GuideLanguage,
    progress: {
      currentStepId?: string | null;
      completedStepIds?: string[];
      quizScores?: Record<string, number>;
    },
  ): Promise<
    ApiResponse<{
      id: string;
      guideId: string;
      currentStepId: string | null;
      completedStepIds: string[];
      quizScores: Record<string, number>;
      isCompleted: boolean;
      startTime: string | null;
      language: string;
      createdAt: string;
      updatedAt: string;
    }>
  > {
    return this.patch(`/guides/${guideId}/progress?language=${language}`, progress);
  }

  async getUserMetrics(language: GuideLanguage): Promise<
    ApiResponse<{
      completedCount: number;
      inProgressCount: number;
      completedCoursesCount: number;
      enrolledCoursesCount: number;
    }>
  > {
    return this.get(`/guides/user/metrics?language=${language}`);
  }

  async getUserUnfinishedGuides(params: {
    language: GuideLanguage;
    cursor?: string;
    limit?: number;
  }): Promise<
    ApiResponse<
      Array<{
        guide: Guide;
        progress: {
          id: string;
          guideId: string;
          currentStepId: string | null;
          completedStepIds: string[];
          quizScores: Record<string, number>;
          isCompleted: boolean;
          startTime: string | null;
          language: string;
          createdAt: string;
          updatedAt: string;
        };
      }>
    >
  > {
    return this.get(`/guides/user/unfinished-guides`, {
      params: {
        language: params.language,
        cursor: params.cursor,
        limit: params.limit,
      },
    });
  }

  async getRecommendedGuides(params: {
    language: GuideLanguage;
    cursor?: string;
    limit?: number;
  }): Promise<ApiResponse<Guide[]>> {
    return this.get(`/guides/user/recommended`, {
      params: {
        language: params.language,
        cursor: params.cursor,
        limit: params.limit,
      },
    });
  }
}

export default new GuidesApi();
